import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { AudioTrack, RoomContext, useAudioPlayback, useConnectionState, useTracks } from '@livekit/components-react'
import { getTrackReferenceId, type TrackReference } from '@livekit/components-core'
import { ConnectionState, Room, RoomEvent, Track, type RoomOptions } from 'livekit-client'
import { Wifi, WifiOff } from 'lucide-react'
import { InlineSkeleton } from '@/components/loading/Skeleton'
import { cn } from '@/lib/utils'
import { LiveKitViewerTelemetry } from './LiveKitViewerTelemetry'
import { Tooltip } from '@/components/play'

type EventLiveKitConfig = {
  eventId: string
  token?: string | null
  serverUrl?: string | null
  roomName?: string | null
  viewerTelemetry?: {
    surface: string
    viewerId?: string | null
    viewerIdentity?: string | null
  } | null
}

type EventLiveKitContextValue = {
  enabled: boolean
  connectionState: ConnectionState
  /** The newest connect failed (not cancelled by a newer token or an unmount); false again once the next one starts. */
  connectFailed: boolean
  /**
   * True while a disconnect this provider made (a new token, no token, unmounting) runs, which is when LiveKit emits its
   * `RoomEvent.Disconnected`. Tells those `CLIENT_INITIATED` disconnects from LiveKit's own: it leaves the room when the
   * page goes (`pagehide`, including into the back-forward cache, and a frozen background tab), reported the same way.
   */
  disconnectIntended: () => boolean
}

type EventLiveKitMediaContextValue = {
  trackRefs: TrackReference[]
  audioTrackRefs: TrackReference[]
}

const EMPTY_TRACK_REFS: TrackReference[] = []
const EMPTY_LIVE_PUBLISHER_IDENTITIES: ReadonlySet<string> = new Set<string>()
const LIVE_VIDEO_SOURCES = [Track.Source.ScreenShare, Track.Source.Camera]
const LIVE_AUDIO_SOURCES = [
  Track.Source.Microphone,
  Track.Source.ScreenShareAudio,
  Track.Source.Unknown,
]
/**
 * What changes which publishers have video published, on top of `useTracks`' own events (participants coming and going,
 * tracks published and unpublished, the connection): a camera pausing or resuming its video.
 */
const PUBLISHED_VIDEO_UPDATE_EVENTS = [RoomEvent.TrackMuted, RoomEvent.TrackUnmuted]
const LIVE_AUDIO_UPDATE_EVENTS = [
  RoomEvent.ParticipantConnected,
  RoomEvent.ParticipantDisconnected,
  RoomEvent.TrackPublished,
  RoomEvent.TrackUnpublished,
  RoomEvent.TrackSubscribed,
  RoomEvent.TrackUnsubscribed,
  RoomEvent.TrackSubscriptionStatusChanged,
  // A source muting its microphone, for a host's honest source volume (docs/features/stage-volume.md).
  RoomEvent.TrackMuted,
  RoomEvent.TrackUnmuted,
]

const NEVER = () => false

const EventLiveKitContext = createContext<EventLiveKitContextValue>({
  enabled: false,
  connectionState: ConnectionState.Disconnected,
  connectFailed: false,
  disconnectIntended: NEVER,
})

const EventLiveKitMediaContext = createContext<EventLiveKitMediaContextValue>({
  trackRefs: EMPTY_TRACK_REFS,
  audioTrackRefs: EMPTY_TRACK_REFS,
})

/**
 * Its own context, apart from the track lists: those change on every track event, while which publishers have live
 * video changes far less often, and a page that only needs the latter shouldn't re-render with the former.
 */
const EventLiveKitLiveIdentitiesContext = createContext<ReadonlySet<string>>(EMPTY_LIVE_PUBLISHER_IDENTITIES)

/** Its own context too, for the same reason: see `useEventLiveKitPublishingIdentities`. */
const EventLiveKitPublishingIdentitiesContext = createContext<ReadonlySet<string>>(EMPTY_LIVE_PUBLISHER_IDENTITIES)

/** Video by source, local included: see `useEventLiveKitVideoSources`. */
export interface EventLiveKitVideoSources {
  live: ReadonlySet<string>
  publishing: ReadonlySet<string>
}

const NO_VIDEO_SOURCES: EventLiveKitVideoSources = { live: EMPTY_LIVE_PUBLISHER_IDENTITIES, publishing: EMPTY_LIVE_PUBLISHER_IDENTITIES }

const EventLiveKitVideoSourcesContext = createContext<EventLiveKitVideoSources>(NO_VIDEO_SOURCES)

export function useEventLiveKit() {
  return useContext(EventLiveKitContext)
}

export function useEventLiveKitTrackRefs() {
  return useContext(EventLiveKitMediaContext).trackRefs
}

export function useEventLiveKitAudioTrackRefs() {
  return useContext(EventLiveKitMediaContext).audioTrackRefs
}

/**
 * The remote publishers with an unmuted, subscribed video track. The same Set while its members stay the same (a new
 * track list with the same live cameras keeps it), so it can sit in dependency lists.
 */
export function useEventLiveKitLivePublisherIdentities(): ReadonlySet<string> {
  return useContext(EventLiveKitLiveIdentitiesContext)
}

/**
 * The remote publishers with an unmuted video track published, whether or not this viewer has it yet: LiveKit lists a
 * room's publications as it connects, and the subscription (and the first frame) follows. A publisher here but not in
 * `useEventLiveKitLivePublisherIdentities` is on its way to this viewer, not offline. Stable like that Set.
 */
export function useEventLiveKitPublishingIdentities(): ReadonlySet<string> {
  return useContext(EventLiveKitPublishingIdentitiesContext)
}

/**
 * Each participant's video by source, the local participant's included (a host sees their own Studio camera and screen
 * as the audience does): a camera (any video but a screen share) by its identity, a screen share as
 * `<identity>:screen` (the watch screen's source ids, `sourceTrack`). `live` has an unmuted track this viewer has (the
 * local one's own); `publishing` an unmuted track published, had yet or not. Stable while their members are, like
 * `useEventLiveKitLivePublisherIdentities`.
 */
export function useEventLiveKitVideoSources(): EventLiveKitVideoSources {
  return useContext(EventLiveKitVideoSourcesContext)
}

const DEFAULT_ROOM_OPTIONS: RoomOptions = {
  adaptiveStream: true,
  dynacast: true,
  videoCaptureDefaults: {
    resolution: { width: 1920, height: 1080 },
  },
}

/**
 * Always provides a LiveKit `Room` context for the current event — whether or
 * not a viewer/admin token is available yet. This means consumers can call
 * `useTracks()`, `useRemoteParticipants()`, etc. unconditionally without
 * crashing during the token-fetch gap. When `config.token` + `config.serverUrl`
 * arrive, the provider connects the existing room; when they clear it
 * disconnects but the context stays mounted.
 *
 * One provider, many surfaces: EventPage, WatchPage, MultiViewPage,
 * EventCard. DRY on purpose — fixing this primitive fixes all of them.
 */
export function EventLiveKitProvider({
  config,
  children,
  renderAudio = true,
}: {
  config?: EventLiveKitConfig | null
  children: ReactNode
  renderAudio?: boolean
}) {
  const token = config?.token ?? null
  const serverUrl = config?.serverUrl ?? null
  const enabled = Boolean(token && serverUrl)

  // One persistent Room per provider instance. The `key` on the caller side
  // controls when we swap events (unmount + remount gives a fresh Room).
  const roomRef = useRef<Room | null>(null)
  if (roomRef.current === null) {
    roomRef.current = new Room(DEFAULT_ROOM_OPTIONS)
  }
  const room = roomRef.current

  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.Disconnected,
  )
  const [connectFailed, setConnectFailed] = useState(false)

  // This provider's own disconnects, counted while they run: LiveKit emits Disconnected inside `disconnect()`, before
  // its promise settles. (One that finds the room already disconnected emits nothing.)
  const ownDisconnectsRef = useRef(0)
  const disconnectRoom = useCallback(() => {
    ownDisconnectsRef.current += 1
    void room.disconnect().finally(() => {
      ownDisconnectsRef.current -= 1
    })
  }, [room])
  const disconnectIntended = useCallback(() => ownDisconnectsRef.current > 0, [])

  // Connect / disconnect based on token availability. Token rotation triggers a
  // reconnect through the effect dependency list.
  useEffect(() => {
    setConnectFailed(false)
    if (!enabled || !token || !serverUrl) {
      if (room.state !== ConnectionState.Disconnected) disconnectRoom()
      return
    }

    let cancelled = false
    let started = false
    setConnectionState(ConnectionState.Connecting)
    // A tick later, so React StrictMode's dev-only unmount, which runs this cleanup straight away, cancels a connect
    // that never started. Aborting LiveKit mid-connect makes its failing attempt close and recreate the room's engine,
    // which can race the next connect.
    const start = setTimeout(() => {
      started = true
      room
        .connect(serverUrl, token)
        .then(() => {
          if (!cancelled) setConnectionState(ConnectionState.Connected)
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            setConnectionState(ConnectionState.Disconnected)
            setConnectFailed(true)
            console.error('[EventLiveKitProvider] connect failed', err)
          }
        })
    }, 0)

    return () => {
      cancelled = true
      clearTimeout(start)
      if (started) disconnectRoom()
    }
  }, [enabled, token, serverUrl, room, disconnectRoom])

  // Dispose the Room instance on unmount so no WebRTC resources leak.
  useEffect(() => {
    return () => {
      if (room.state !== ConnectionState.Disconnected) disconnectRoom()
    }
  }, [room, disconnectRoom])

  const contextValue = useMemo<EventLiveKitContextValue>(() => ({
    enabled,
    connectionState,
    connectFailed,
    disconnectIntended,
  }), [enabled, connectionState, connectFailed, disconnectIntended])

  return (
    <EventLiveKitContext.Provider value={contextValue}>
      <RoomContext.Provider value={room}>
        <EventLiveKitMediaProvider>
          <EventLiveKitAudioRenderer enabled={renderAudio} />
          <ConnectionStateBridge onChange={setConnectionState} />
          {config?.viewerTelemetry ? (
            // A new identity (sign-in, a refresh after removal) is a new viewer session, not a failure of the old one.
            <LiveKitViewerTelemetry
              key={config.viewerTelemetry.viewerIdentity ?? config.viewerTelemetry.viewerId}
              eventId={config.eventId}
              surface={config.viewerTelemetry.surface}
              viewerId={config.viewerTelemetry.viewerId}
              viewerIdentity={config.viewerTelemetry.viewerIdentity}
            />
          ) : null}
          {children}
        </EventLiveKitMediaProvider>
      </RoomContext.Provider>
    </EventLiveKitContext.Provider>
  )
}

export function useLiveKitAudioReady(enabled = true) {
  const { canPlayAudio, startAudio } = useAudioPlayback()
  const pendingStartRef = useRef(false)

  useEffect(() => {
    if (!enabled || canPlayAudio || typeof window === 'undefined') return

    const startFromGesture = () => {
      if (pendingStartRef.current) return
      pendingStartRef.current = true
      void startAudio()
        .catch(() => {
          // LiveKit logs the concrete playback failure. Keep listening so the
          // next real gesture can unlock the browser audio context.
        })
        .finally(() => {
          pendingStartRef.current = false
        })
    }

    const options: AddEventListenerOptions = { capture: true }
    window.addEventListener('pointerdown', startFromGesture, options)
    window.addEventListener('click', startFromGesture, options)
    window.addEventListener('keydown', startFromGesture, options)
    window.addEventListener('touchend', startFromGesture, options)

    return () => {
      window.removeEventListener('pointerdown', startFromGesture, options)
      window.removeEventListener('click', startFromGesture, options)
      window.removeEventListener('keydown', startFromGesture, options)
      window.removeEventListener('touchend', startFromGesture, options)
    }
  }, [canPlayAudio, enabled, startAudio])

  return enabled && canPlayAudio
}

/**
 * How loud each participant's audio plays, 0–1, by identity: LiveKit's per-track volume (`RemoteAudioTrack.setVolume`),
 * which `AudioTrack` sets on the element again after every attach. iOS and iPadOS ignore an element's volume, so there a
 * level between 0 and 1 plays in full (docs/features/stage-volume.md). A track at 0 (the viewer muted, or a host's
 * source turned off) isn't played at all, which holds on every browser: it keeps its subscription, so it comes back at
 * once, but has no element (attaching a track unmutes its element, so muting one wouldn't hold). Left out, tracks play
 * as LiveKit has them.
 */
export type EventLiveKitVolumeFor = (identity: string) => number

export function EventLiveKitAudioRenderer({ enabled = true, volumeFor }: { enabled?: boolean; volumeFor?: EventLiveKitVolumeFor }) {
  const audioReady = useLiveKitAudioReady(enabled)

  return <EventLiveKitAudioElements enabled={audioReady} volumeFor={volumeFor} />
}

export function EventLiveKitAudioElements({ enabled, volumeFor }: { enabled: boolean; volumeFor?: EventLiveKitVolumeFor }) {
  const audioTrackRefs = useEventLiveKitAudioTrackRefs()

  const playableTrackRefs = useMemo(
    () =>
      enabled
        ? audioTrackRefs.filter(
            (trackRef) =>
              !trackRef.participant.isLocal &&
              trackRef.publication.kind === Track.Kind.Audio &&
              trackRef.publication.isSubscribed &&
              volumeFor?.(trackRef.participant.identity) !== 0,
          )
        : EMPTY_TRACK_REFS,
    [audioTrackRefs, enabled, volumeFor],
  )

  if (!enabled || playableTrackRefs.length === 0) return null

  return (
    <div style={{ display: 'none' }}>
      {playableTrackRefs.map((trackRef) => (
        <AudioTrack
          key={getTrackReferenceId(trackRef)}
          trackRef={trackRef}
          volume={volumeFor?.(trackRef.participant.identity)}
        />
      ))}
    </div>
  )
}

export function EventLiveKitMediaProvider({ children }: { children: ReactNode }) {
  const trackRefs = useTracks(LIVE_VIDEO_SOURCES, { onlySubscribed: true })
  const publishedRefs = useTracks(LIVE_VIDEO_SOURCES, { updateOnlyOn: PUBLISHED_VIDEO_UPDATE_EVENTS, onlySubscribed: false })
  const audioTrackRefs = useTracks(LIVE_AUDIO_SOURCES, {
    updateOnlyOn: LIVE_AUDIO_UPDATE_EVENTS,
    onlySubscribed: false,
  })

  const livePublisherIdentities = useUnmutedRemoteIdentities(trackRefs)
  const publishingIdentities = useUnmutedRemoteIdentities(publishedRefs)
  const liveSources = useUnmutedSources(trackRefs)
  const publishingSources = useUnmutedSources(publishedRefs)
  const videoSources = useMemo<EventLiveKitVideoSources>(
    () => ({ live: liveSources, publishing: publishingSources }),
    [liveSources, publishingSources],
  )

  const value = useMemo<EventLiveKitMediaContextValue>(() => ({
    trackRefs,
    audioTrackRefs,
  }), [trackRefs, audioTrackRefs])

  return (
    <EventLiveKitMediaContext.Provider value={value}>
      <EventLiveKitLiveIdentitiesContext.Provider value={livePublisherIdentities}>
        <EventLiveKitPublishingIdentitiesContext.Provider value={publishingIdentities}>
          <EventLiveKitVideoSourcesContext.Provider value={videoSources}>{children}</EventLiveKitVideoSourcesContext.Provider>
        </EventLiveKitPublishingIdentitiesContext.Provider>
      </EventLiveKitLiveIdentitiesContext.Provider>
    </EventLiveKitMediaContext.Provider>
  )
}

/** The unmuted video among `refs` by source (`useEventLiveKitVideoSources`), the local participant's too. */
function useUnmutedSources(refs: TrackReference[]): ReadonlySet<string> {
  return useStableSet(
    refs
      .filter((ref) => !ref.publication.isMuted)
      .map((ref) => (ref.source === Track.Source.ScreenShare ? `${ref.participant.identity}:screen` : ref.participant.identity)),
  )
}

/**
 * The remote participants with an unmuted track among `refs`. Keyed on the sorted identities, so the Set only changes
 * when its members do.
 */
function useUnmutedRemoteIdentities(refs: TrackReference[]): ReadonlySet<string> {
  return useStableSet(refs.filter((ref) => !ref.participant.isLocal && !ref.publication.isMuted).map((ref) => ref.participant.identity))
}

/** `values` as a Set keyed on its sorted members, so it only changes when they do. */
function useStableSet(values: string[]): ReadonlySet<string> {
  const key = JSON.stringify(Array.from(new Set(values)).sort())
  return useMemo<ReadonlySet<string>>(() => {
    const members = JSON.parse(key) as string[]
    return members.length ? new Set(members) : EMPTY_LIVE_PUBLISHER_IDENTITIES
  }, [key])
}

function ConnectionStateBridge({ onChange }: { onChange: (next: ConnectionState) => void }) {
  const state = useConnectionState()

  useEffect(() => {
    onChange(state)
  }, [state, onChange])

  return null
}

const STATE_META: Partial<Record<ConnectionState, { label: string; tone: 'neutral' | 'success' | 'warning' | 'error' }>> = {
  [ConnectionState.Disconnected]: { label: 'Preview offline', tone: 'error' },
  [ConnectionState.Connecting]: { label: 'Connecting…', tone: 'warning' },
  [ConnectionState.Connected]: { label: 'Connected', tone: 'success' },
  [ConnectionState.Reconnecting]: { label: 'Reconnecting…', tone: 'warning' },
  [ConnectionState.SignalReconnecting]: { label: 'Signal reconnect…', tone: 'warning' },
}

// The status glyph is focusable, so it keeps a 44px target: it looks 32px and an invisible hit area adds 6px a side,
// as on a compact Button.
const STATUS_GLYPH_CLASS =
  `relative inline-flex h-8 w-8 items-center justify-center rounded-full after:absolute after:-inset-1.5 after:content-[''] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-10 focus-visible:ring-offset-2`

function toneClassName(tone: 'neutral' | 'success' | 'warning' | 'error') {
  switch (tone) {
    case 'success':
      // A connected LiveKit room is the live state, so it takes the live green.
      return 'bg-color-3 text-neutral-10'
    case 'warning':
      return 'bg-warning-transparent text-warning'
    case 'error':
      return 'bg-error-transparent text-error'
    default:
      return 'bg-neutral-3 text-neutral-7'
  }
}

export function LiveKitConnectionStatus({ className }: { className?: string }) {
  const { enabled, connectionState } = useEventLiveKit()

  if (!enabled) {
    return (
      <Tooltip content="Live preview unavailable">
        {/* A focusable, named glyph, so keyboard and screen reader users get the state too. */}
        <span
          role="img"
          tabIndex={0}
          aria-label="Live preview unavailable"
          className={cn(STATUS_GLYPH_CLASS, 'bg-neutral-3 text-neutral-7', className)}
        >
          <WifiOff size={14} aria-hidden="true" />
        </span>
      </Tooltip>
    )
  }

  const meta = STATE_META[connectionState] ?? { label: connectionState, tone: 'neutral' as const }

  // Determine which icon to show based on connection state
  const Icon =
    connectionState === ConnectionState.Connected ? Wifi :
    connectionState === ConnectionState.Disconnected ? WifiOff :
    null

  return (
    <Tooltip content={meta.label}>
      <span
        role="img"
        tabIndex={0}
        aria-label={meta.label}
        className={cn(STATUS_GLYPH_CLASS, 'transition-colors', toneClassName(meta.tone), className)}
        data-livekit-state={connectionState}
      >
        {Icon ? <Icon size={14} aria-hidden="true" /> : <InlineSkeleton label={meta.label} className="h-3.5 w-3.5" />}
      </span>
    </Tooltip>
  )
}
